import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  HostListener,
  SimpleChanges,
  ElementRef,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { trigger, transition, style, animate } from '@angular/animations';
import { DEFAULT_TIME_SLOTS } from '../../constants/time-slots.constant';
import { addMinutesToDate, parseTimeToMinutes } from '../../utils/time.util';
import { DropdownToggleHelper } from '../../helpers/dropdown-toggle.helper';

export type DatePickerMode = 'date' | 'datetime' | 'time';

export interface TimeSlot {
  time: string;
  displayTime: string;
  available: boolean;
}

/**
 * CustomDatePickerComponent - a reusable date and time picker with calendar UI, selectable time slots, dropdown positioning and built-in availability rules. Integrates with Angular forms via ControlValueAccessor.
 * @component
 * @selector app-custom-date-picker
 * @example
 *   <app-custom-date-picker
 *     [placeholder]="'Select date and time'"
 *     [showTime]="true"
 *     [timeSlots]="timeSlots"
 *     [locationDetails]="location"
 *     (dateTimeChange)="onDateTimeChange($event)"
 *     (timeSlotChange)="onTimeSlotChange($event)">
 *   </app-custom-date-picker>
 * @input {string} placeholder - Placeholder text shown when no date/time is selected (default: 'Select date and time').
 * @input {boolean} showTime - Toggle between date-only mode and date+time mode; when true time selection is required.
 * @input {boolean} disabled - Disables user interaction with the picker when true.
 * @input {Date|null} minDate - Optional minimum selectable date.
 * @input {Date|null} maxDate - Optional maximum selectable date.
 * @input {TimeSlot[]} timeSlots - Array of available time slots; falls back to internal default slots when empty.
 * @input {string} selectedTimeSlot - Initial selected time slot string (used to pre-select a time slot).
 * @input {any} locationDetails - Optional location operating hours and availability used to enable/disable specific days and time slots.
 * @input {boolean} showFooterButtons - Show or hide Cancel/Continue footer buttons in the dropdown (default: true).
 * @input {boolean} allowPastDates - When true, allows selection of past dates (useful for analytics or historical selection).
 * @output {EventEmitter<Date>} dateTimeChange - Emits the selected Date (with time when showTime is true, or start of day when false). Emits null when cleared.
 * @output {EventEmitter<string>} timeSlotChange - Emits the selected time slot string when the time changes (emits empty string on clear).
 *
 * Purpose:
 *   - Provide a compact, accessible date + optional time picker with calendar navigation, time-slot selection, and automatic dropdown positioning (including mobile centering).
 *   - Respect location operating hours to enable/disable days and individual time slots.
 *   - Work as a ControlValueAccessor so it can be used with ngModel/reactive forms.
 *
 * Key methods:
 *   - togglePicker(): Open/close the picker dropdown and position it; respects disabled state.
 *   - positionDropdown(): Intelligent dropdown placement logic (below/above/center) with viewport boundary adjustments.
 *   - onDateSelect(date): Handle user date selection, prevent past-date selection when not allowed, and trigger updates/touch events.
 *   - onTimeSelect(timeSlot): Handle time slot selection, validate availability and past-time constraints.
 *   - updateValue(): Compose Date (and time) or date-only value, call registered onChange, and emit dateTimeChange/timeSlotChange.
 *   - clearSelection(): Clear current date/time selection and emit change events.
 *   - cancelSelection(): Restore the value from when the picker opened, close, and mark touched.
 *   - applySelection(): Apply the current selection, close the picker and mark touched.
 *   - generateCalendarDays(): Build the calendar grid for the currentMonth including previous/next month spillover days.
 *   - previousMonth()/nextMonth(): Navigate calendar and regenerate days.
 *   - getDisplayValue(): Human-readable display for the input based on current selection and time slot display label.
 *   - isDateDisabled()/isDayAvailable()/isTimeSlotAvailableForDate(): Availability checks based on allowPastDates and locationDetails hours.
 *   - isPastDate()/isPastTime(): Helpers to prevent selection of past dates/times when appropriate.
 *   - formatTime()/formatDate()/getCurrentMonthYear(): Formatting helpers for display.
 *
 * Lifecycle hooks & integrations:
 *   - ngOnInit(): Initializes default time slots when none are provided and generates the initial calendar days.
 *   - Implements ControlValueAccessor: writeValue, registerOnChange, registerOnTouched, setDisabledState to integrate with forms.
 *   - Host listener (document:click): Closes the picker when clicking outside the component.
 *
 * Notes:
 *   - The component supplies NG_VALUE_ACCESSOR so it can be used as a form control.
 *   - Dropdown uses a small enter/leave animation and adapts layout for mobile (centers modal-like on small screens).
 */
@Component({
  selector: 'app-custom-date-picker',
  templateUrl: './custom-date-picker.component.html',
  styleUrls: ['./custom-date-picker.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDatepickerModule, MatButtonModule],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-10px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px)' })),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerComponent),
      multi: true,
    },
  ],
})
export class CustomDatePickerComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  private static nextOverlayId = 0;
  private readonly overlayId = `cdp-${CustomDatePickerComponent.nextOverlayId++}`;

  @Input() placeholder = 'Select date and time';
  /** Primary mode: `date`, `datetime`, or `time`. Falls back to `showTime` when unset. */
  @Input() mode: DatePickerMode | null = null;
  /** @deprecated Prefer `mode`. When `mode` is unset, `true` → datetime and `false` → date. */
  @Input() showTime = true;
  @Input() disabled = false;
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() timeSlots: TimeSlot[] = [];
  @Input() selectedTimeSlot = '';
  @Input() locationDetails: any = null; // Location details with operating hours
  @Input() showFooterButtons = true; // Show Cancel/Continue buttons
  @Input() allowPastDates = false; // Allow selection of past dates (useful for analytics)
  /**
   * When > 0, time slots on today's date that are less than this many minutes
   * from now are disabled. Applied independently of `allowPastDates` so
   * Start/End can enforce a lead time while From/To stay unrestricted.
   */
  @Input() minLeadMinutes = 0;
  /** Anchor calendar under the input: `left` (default) or `right` (End Date in range pickers). */
  @Input() dropdownAlign: 'left' | 'right' = 'left';
  /**
   * When true, positions the calendar with `position: fixed` under the input and
   * appends the panel to `document.body` so modal/overflow ancestors cannot clip it.
   * Use inside nested dropdowns or modals (e.g. Create Discount, period Custom range).
   */
  @Input() fixedOverlay = false;
  /** When true, shows a clear control when a date/time value is selected. */
  @Input() showClear = false;

  @Output() dateTimeChange = new EventEmitter<Date>();
  @Output() timeSlotChange = new EventEmitter<string>();

  @ViewChild('pickerInput') private pickerInput?: ElementRef<HTMLElement>;
  @ViewChild('pickerDropdown') private pickerDropdown?: ElementRef<HTMLElement>;

  isOpen = false;
  /** True after fixed-overlay top/left have been applied (prevents top-left flash). */
  fixedOverlayReady = false;
  private overlayScrollParents: EventTarget[] = [];
  private dropdownHomeParent: HTMLElement | null = null;
  private dropdownHomeNextSibling: ChildNode | null = null;
  private readonly onOverlayReposition = (): void => {
    if (this.isOpen && this.fixedOverlay) {
      this.applyDropdownPosition();
    }
  };
  selectedDate: Date | null = null;
  selectedTime = '';
  currentMonth: Date = new Date();

  // Calendar properties
  weekDays: string[] = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  calendarDays: any[] = [];

  // ControlValueAccessor properties
  private onChange: (value: Date | null) => void = () => {};
  private onTouched: () => void = () => {};
  private controlValue: Date | null = null;
  /** Snapshot of the CVA value when the picker opened; restored by cancelSelection(). */
  private valueAtOpen: Date | null = null;
  private readonly zonedDateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

  // Default time slots if none provided
  defaultTimeSlots: TimeSlot[] = [...DEFAULT_TIME_SLOTS];

  constructor(
    private readonly hostRef: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef
  ) {}

  get effectiveMode(): DatePickerMode {
    if (this.mode) {
      return this.mode;
    }
    return this.showTime ? 'datetime' : 'date';
  }

  get showsCalendar(): boolean {
    return this.effectiveMode !== 'time';
  }

  get showsTimePicker(): boolean {
    return this.effectiveMode !== 'date';
  }

  get showClearButton(): boolean {
    if (!this.showClear || this.disabled) {
      return false;
    }
    if (this.effectiveMode === 'time') {
      return Boolean(this.selectedTime);
    }
    if (this.effectiveMode === 'date') {
      return Boolean(this.selectedDate);
    }
    return Boolean(this.selectedDate && this.selectedTime);
  }

  ngOnInit(): void {
    // Use provided time slots or default ones
    if (this.timeSlots.length === 0) {
      this.timeSlots = this.defaultTimeSlots;
    }
    this.syncSelectedTimeSlotInput();
    this.generateCalendarDays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTimeSlot'] && !changes['selectedTimeSlot'].firstChange) {
      this.syncSelectedTimeSlotInput();
    }

    if (changes['locationDetails']) {
      const prevTimezone = this.normalizeTimezone(
        changes['locationDetails'].previousValue?.timezone
      );
      const currTimezone = this.normalizeTimezone(
        changes['locationDetails'].currentValue?.timezone
      );
      // Ignore unstable parent object refs that carry the same timezone.
      if (prevTimezone === currTimezone) {
        return;
      }
      if (this.controlValue) {
        this.applyControlValue(this.controlValue);
      }
      this.generateCalendarDays();
    }
  }

  /** Sync optional HH:mm input used by time-only consumers (e.g. discount From/To). */
  private syncSelectedTimeSlotInput(): void {
    if (this.selectedTimeSlot === this.selectedTime) {
      return;
    }
    this.selectedTime = this.selectedTimeSlot || '';
  }

  private normalizeTimezone(timezone: any): string {
    return typeof timezone === 'string' ? timezone.trim() : '';
  }

  // ControlValueAccessor implementation
  writeValue(value: Date | null): void {
    this.controlValue = value;
    this.applyControlValue(value);
  }

  private applyControlValue(value: Date | null): void {
    const timezone = this.getLocationTimezone();
    if (value && this.showsCalendar) {
      this.selectedDate = timezone ? this.getDateInTimezone(value, timezone) : value;
    } else if (value && this.effectiveMode === 'time') {
      this.selectedDate = timezone ? this.getDateInTimezone(value, timezone) : new Date(value);
    } else {
      this.selectedDate = null;
    }

    if (value && this.showsTimePicker) {
      this.selectedTime = timezone
        ? this.formatTimeInTimezone(value, timezone)
        : this.formatTime(value);
    } else {
      this.selectedTime = '';
    }
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Toggle the date picker open state; if not disabled, open/close the picker, position the dropdown when opened, and mark the control as touched when closed.
   * @example
   * ogglePicker()
   * undefined
   * @returns {void} No return value.
   */
  togglePicker(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }
    if (this.isOpen) {
      this.closePicker();
      this.onTouched();
      return;
    }
    this.openPicker();
  }

  /** Close this picker without marking the control touched. */
  closePicker(): void {
    if (!this.isOpen) {
      return;
    }
    this.isOpen = false;
    this.teardownOverlay();
    DropdownToggleHelper.close(this.overlayId);
  }

  private openPicker(): void {
    // Capture pre-open value so Cancel can discard in-session selections.
    this.valueAtOpen = this.controlValue ? new Date(this.controlValue.getTime()) : null;
    this.fixedOverlayReady = false;
    DropdownToggleHelper.open(this.overlayId, () => this.closePicker(), this.hostRef.nativeElement);
    this.isOpen = true;
    this.cdr.detectChanges();
    if (typeof window !== 'undefined' && (this.fixedOverlay || window.innerWidth < 768)) {
      this.positionDropdown();
    } else {
      this.scrollRelevantTimeSlotIntoView();
    }
  }

  /**
   * Keep the meaningful time slot in view when the picker opens:
   * - Edit / existing selection → selected slot
   * - Create / no selection → first enabled slot (next valid after minDate / now)
   * Full slot list stays rendered; only scroll position changes.
   */
  private scrollRelevantTimeSlotIntoView(): void {
    if (!this.showsTimePicker) {
      return;
    }
    requestAnimationFrame(() => {
      const panel = this.pickerDropdown?.nativeElement;
      if (!panel) {
        return;
      }
      const selected = panel.querySelector('.time-slot.selected') as HTMLElement | null;
      if (selected) {
        selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        return;
      }
      const firstEnabled = panel.querySelector('.time-slot:not(:disabled)') as HTMLElement | null;
      firstEnabled?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  private positionDropdown(): void {
    setTimeout(() => {
      if (!this.isOpen) {
        return;
      }
      this.applyDropdownPosition();
      requestAnimationFrame(() => this.applyDropdownPosition());
      if (this.fixedOverlay) {
        this.bindOverlayRepositionListeners();
      }
      this.scrollRelevantTimeSlotIntoView();
    }, 0);
  }

  private teardownOverlay(): void {
    this.unbindOverlayRepositionListeners();
    this.restoreDropdownHome();
    this.clearFixedDropdownStyles();
    this.fixedOverlayReady = false;
  }

  private applyDropdownPosition(): void {
    const host = this.hostRef.nativeElement;
    const dropdownElement =
      this.pickerDropdown?.nativeElement ??
      (host.querySelector('.date-picker-dropdown') as HTMLElement | null);

    if (!dropdownElement) {
      return;
    }

    if (this.fixedOverlay) {
      this.attachDropdownToBody(dropdownElement);
    }

    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      dropdownElement.style.position = 'fixed';
      dropdownElement.style.top = '50%';
      dropdownElement.style.left = '50%';
      dropdownElement.style.right = 'auto';
      dropdownElement.style.transform = 'translate(-50%, -50%)';
      this.fixedOverlayReady = true;
      return;
    }

    if (!this.fixedOverlay) {
      return;
    }

    const inputEl = this.pickerInput?.nativeElement ?? host;
    const rect = inputEl.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 8;
    const preferredWidth = this.showsTimePicker ? 520 : 300;
    const measuredWidth = dropdownElement.getBoundingClientRect().width;
    const dropdownWidth = Math.min(
      Math.max(
        this.effectiveMode === 'time' ? rect.width : measuredWidth || preferredWidth,
        this.effectiveMode === 'time' ? rect.width : preferredWidth
      ),
      window.innerWidth - viewportPadding * 2
    );
    const dropdownHeight = dropdownElement.offsetHeight || 320;

    let top = rect.bottom + gap;
    if (top + dropdownHeight > window.innerHeight - viewportPadding) {
      top = Math.max(viewportPadding, rect.top - dropdownHeight - gap);
    }

    let left = this.dropdownAlign === 'right' ? rect.right - dropdownWidth : rect.left;
    left = Math.min(
      Math.max(viewportPadding, left),
      window.innerWidth - dropdownWidth - viewportPadding
    );

    dropdownElement.style.position = 'fixed';
    dropdownElement.style.top = `${top}px`;
    dropdownElement.style.left = `${left}px`;
    dropdownElement.style.right = 'auto';
    dropdownElement.style.transform = 'none';
    dropdownElement.style.zIndex = '14000';
    dropdownElement.style.width = `${dropdownWidth}px`;
    this.fixedOverlayReady = true;
  }

  /** Move the panel under `body` so overflow / stacking on ancestors cannot clip it. */
  private attachDropdownToBody(dropdownElement: HTMLElement): void {
    if (typeof document === 'undefined') {
      return;
    }
    if (dropdownElement.parentElement === document.body) {
      DropdownToggleHelper.setActivePanel(this.overlayId, dropdownElement);
      return;
    }
    this.dropdownHomeParent = dropdownElement.parentElement;
    this.dropdownHomeNextSibling = dropdownElement.nextSibling;
    document.body.appendChild(dropdownElement);
    DropdownToggleHelper.setActivePanel(this.overlayId, dropdownElement);
  }

  private restoreDropdownHome(): void {
    const dropdown = this.pickerDropdown?.nativeElement;
    if (!dropdown || !this.dropdownHomeParent) {
      this.dropdownHomeParent = null;
      this.dropdownHomeNextSibling = null;
      return;
    }
    if (dropdown.parentElement === document.body) {
      this.dropdownHomeParent.insertBefore(dropdown, this.dropdownHomeNextSibling);
    }
    this.dropdownHomeParent = null;
    this.dropdownHomeNextSibling = null;
  }

  private clearFixedDropdownStyles(): void {
    const dropdown = this.pickerDropdown?.nativeElement;
    if (!dropdown || !this.fixedOverlay) {
      return;
    }
    dropdown.style.position = '';
    dropdown.style.top = '';
    dropdown.style.left = '';
    dropdown.style.right = '';
    dropdown.style.width = '';
    dropdown.style.transform = '';
    dropdown.style.zIndex = '';
  }

  private bindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    this.unbindOverlayRepositionListeners();
    window.addEventListener('resize', this.onOverlayReposition);
    const scrollParents = this.collectScrollParents(this.hostRef.nativeElement);
    this.overlayScrollParents = scrollParents;
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', this.onOverlayReposition, true);
    }
  }

  private unbindOverlayRepositionListeners(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('resize', this.onOverlayReposition);
    for (const parent of this.overlayScrollParents) {
      parent.removeEventListener('scroll', this.onOverlayReposition, true);
    }
    this.overlayScrollParents = [];
  }

  private collectScrollParents(element: HTMLElement): EventTarget[] {
    const parents: EventTarget[] = [window];
    let node: HTMLElement | null = element.parentElement;
    while (node) {
      const style = window.getComputedStyle(node);
      const overflowValues = `${style.overflow}|${style.overflowY}|${style.overflowX}`;
      if (/(auto|scroll|overlay)/.test(overflowValues)) {
        parents.push(node);
      }
      node = node.parentElement;
    }
    return parents;
  }

  /**
   * Selects a date for the custom date picker, applies validation for past dates, updates the internal value, and optionally closes the picker when in date-only mode.
   * @example
   * nDateSelect(new Date('2026-03-17'))
   * undefined
   * @param {{Date}} {{date}} - Date to select; selection is ignored if past dates are not allowed and the date is in the past.
   * @returns {{void}} No return value.
   **/
  onDateSelect(date: Date): void {
    // Check if the date is in the past (only if past dates are not allowed)
    if (!this.allowPastDates && this.isPastDate(date)) {
      return; // Don't allow selection of past dates
    }
    if (this.isOutsideMinMaxRange(date)) {
      return;
    }
    this.selectedDate = date;
    if (this.effectiveMode === 'date') {
      this.updateValue();
      this.closePicker();
      this.onTouched();
    } else {
      this.updateValue();
      // Date change can move the first enabled slot (e.g. today vs future).
      this.cdr.detectChanges();
      this.scrollRelevantTimeSlotIntoView();
    }
  }

  onTimeSelect(timeSlot: TimeSlot): void {
    const selectedDate = this.selectedDate || this.getCurrentDate();
    if (this.isTimeSlotDisabled(timeSlot)) {
      return;
    }
    if (
      this.isTimeSlotAvailableForDate(timeSlot, selectedDate) &&
      (this.allowPastDates || !this.isPastTime(timeSlot.time, selectedDate))
    ) {
      if (this.effectiveMode === 'time' && !this.selectedDate) {
        this.selectedDate = selectedDate;
      }
      this.selectedTime = timeSlot.time;
      this.updateValue();
      this.onTouched();
      if (this.effectiveMode === 'time' && !this.showFooterButtons) {
        this.closePicker();
      }
    }
  }

  /**
   * Update the component's value and emit corresponding events based on the current selectedDate, selectedTime, and showTime flag.
   * @example
   * updateValue()
   * void
   * @returns {void} No return value; updates internal state and emits dateTimeChange (and timeSlotChange when applicable) and calls onChange.
   */
  private updateValue(): void {
    if (this.effectiveMode === 'time') {
      if (!this.selectedTime) {
        return;
      }
      const baseDate = this.selectedDate || this.getCurrentDate();
      const [hours, minutes] = this.selectedTime.split(':').map(Number);
      const timezone = this.getLocationTimezone();
      const dateTime = timezone
        ? this.createDateInTimezone(baseDate, hours, minutes, timezone)
        : (() => {
            const dt = new Date(baseDate);
            dt.setHours(hours, minutes, 0, 0);
            return dt;
          })();
      this.controlValue = dateTime;
      this.onChange(dateTime);
      this.dateTimeChange.emit(dateTime);
      this.timeSlotChange.emit(this.selectedTime);
      return;
    }

    if (this.showsTimePicker) {
      if (this.selectedDate && this.selectedTime) {
        const [hours, minutes] = this.selectedTime.split(':').map(Number);
        const dateTime = this.createSelectedDateTime(hours, minutes);

        this.controlValue = dateTime;
        this.onChange(dateTime);
        this.dateTimeChange.emit(dateTime);
        this.timeSlotChange.emit(this.selectedTime);
      }
      return;
    }

    if (this.selectedDate) {
      const timezone = this.getLocationTimezone();
      const dateOnly = timezone
        ? this.createDateInTimezone(this.selectedDate, 0, 0, timezone)
        : new Date(this.selectedDate);
      if (!timezone) {
        dateOnly.setHours(0, 0, 0, 0);
      }
      this.controlValue = dateOnly;
      this.onChange(dateOnly);
      this.dateTimeChange.emit(dateOnly);
    }
  }

  private createSelectedDateTime(hours: number, minutes: number): Date {
    const timezone = this.getLocationTimezone();
    if (timezone && this.selectedDate) {
      return this.createDateInTimezone(this.selectedDate, hours, minutes, timezone);
    }

    const dateTime = new Date(this.selectedDate as Date);
    dateTime.setHours(hours, minutes, 0, 0);
    return dateTime;
  }

  private createDateInTimezone(date: Date, hours: number, minutes: number, timezone: string): Date {
    const { year, month, day } = this.getDateParts(date);
    const wallTimeUtc = Date.UTC(year, month - 1, day, hours, minutes, 0, 0);
    let timestamp = wallTimeUtc;

    // Resolve the UTC instant for this shop-local wall time, including DST offsets.
    for (let i = 0; i < 2; i += 1) {
      timestamp = wallTimeUtc - this.getTimezoneOffsetMs(new Date(timestamp), timezone);
    }

    return new Date(timestamp);
  }

  private getTimezoneOffsetMs(date: Date, timezone: string): number {
    const parts = this.getZonedDateTimeParts(date, timezone);
    const zonedAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    return zonedAsUtc - date.getTime();
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private formatTimeInTimezone(date: Date, timezone: string): string {
    const { hour, minute } = this.getZonedDateTimeParts(date, timezone);
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  private getDateInTimezone(date: Date, timezone: string): Date {
    const { year, month, day } = this.getZonedDateTimeParts(date, timezone);
    return new Date(year, month - 1, day);
  }

  private formatDate(date: Date): string {
    const day = date.getDate();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${month} ${day} ${year}`;
  }

  /**
   * Return formatted display string for the currently selected date and optional time.
   * @example
   * etDisplayValue()
   * "Jan 15 2024" // date-only mode
   * etDisplayValue()
   * "Jan 15 2024 2:00 PM" // date + time mode (uses matching timeSlots displayTime if available)
   * @returns {string} Formatted display string for the selected date/time, or an empty string if no date is selected.
   **/
  getDisplayValue(): string {
    if (this.effectiveMode === 'time') {
      if (!this.selectedTime) {
        return '';
      }
      const timeSlot = this.timeSlots.find(slot => slot.time === this.selectedTime);
      return timeSlot?.displayTime || this.selectedTime;
    }

    if (!this.selectedDate) {
      return '';
    }

    if (this.showsTimePicker && this.selectedTime) {
      const dateStr = this.formatDate(this.selectedDate);
      const timeSlot = this.timeSlots.find(slot => slot.time === this.selectedTime);
      return `${dateStr} ${timeSlot?.displayTime || this.selectedTime}`;
    }

    return this.formatDate(this.selectedDate);
  }

  onClearClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled || !this.showClearButton) {
      return;
    }
    this.clearSelection();
    this.closePicker();
  }

  clearSelection(): void {
    this.selectedDate = null;
    this.selectedTime = '';
    this.controlValue = null;
    this.onChange(null);
    this.dateTimeChange.emit(null as any);
    this.timeSlotChange.emit('');
    this.onTouched(); // Mark as touched when clearing
  }

  /** Discard in-session date/time picks and restore the value from when the picker opened. */
  cancelSelection(): void {
    const restored = this.valueAtOpen;
    this.controlValue = restored;
    this.applyControlValue(restored);
    this.onChange(restored);
    if (restored) {
      this.dateTimeChange.emit(restored);
      this.timeSlotChange.emit(this.selectedTime);
    } else {
      this.dateTimeChange.emit(null as any);
      this.timeSlotChange.emit('');
    }
    this.closePicker();
    this.onTouched();
  }

  applySelection(): void {
    this.closePicker();
    this.onTouched(); // Mark as touched when applying selection
    this.updateValue();
  }

  isTimeSlotSelected(timeSlot: TimeSlot): boolean {
    return this.selectedTime === timeSlot.time;
  }

  // Close picker when clicking outside this instance (not another sibling picker / body panel).
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as Node;
    const panel = this.pickerDropdown?.nativeElement;
    if (this.hostRef.nativeElement.contains(target) || panel?.contains(target)) {
      return;
    }
    this.closePicker();
  }

  ngOnDestroy(): void {
    if (this.isOpen) {
      this.closePicker();
      return;
    }
    this.teardownOverlay();
  }

  // Calendar navigation methods
  previousMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1
    );
    this.generateCalendarDays();
  }

  nextMonth(): void {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1
    );
    this.generateCalendarDays();
  }

  getCurrentMonthYear(): string {
    return this.currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  /**
   * Populate the component's calendarDays array with a full-week grid of day objects for the month in this.currentMonth.
   * @example
   * generateCalendarDays()
   * // this.calendarDays => [
   * //   { date: new Date(2026, 2, 29), dayNumber: 29, isCurrentMonth: false },
   * //   { date: new Date(2026, 3, 1), dayNumber: 1, isCurrentMonth: true },
   * //   { date: new Date(2026, 3, 2), dayNumber: 2, isCurrentMonth: true },
   * //   ...
   * // ]
   * @returns {void} Updates this.calendarDays with objects { date, dayNumber, isCurrentMonth } and does not return a value.
   **/
  generateCalendarDays(): void {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

    this.calendarDays = [];
    let currentDate = new Date(startDate);
    const end = endDate.getTime();
    while (currentDate.getTime() <= end) {
      this.calendarDays.push({
        date: new Date(currentDate),
        dayNumber: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === month,
      });
      currentDate = new Date(currentDate);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  isSelectedDate(date: Date): boolean {
    if (!this.selectedDate) return false;
    return date.toDateString() === this.selectedDate.toDateString();
  }

  isToday(date: Date): boolean {
    const timezone = this.getLocationTimezone();
    if (timezone) {
      return this.getDateKey(date) === this.getShopTodayKey(timezone);
    }

    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isPastDate(date: Date): boolean {
    const timezone = this.getLocationTimezone();
    if (timezone) {
      return this.getDateKey(date) < this.getShopTodayKey(timezone);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0); // Reset time to start of day
    return compareDate < today;
  }

  /**
   * Determine whether the provided time string refers to a past or current time today.
   * @example
   * sPastTime('14:30')
   * true
   * @param {{string}} {{time}} - Time string in "HH:MM" 24-hour format to compare with current time.
   * @returns {{boolean}} Returns true if the time is less than or equal to now and the component's selectedDate is today; otherwise false.
   **/
  isPastTime(time: string, date: Date = this.selectedDate || this.getCurrentDate()): boolean {
    if (!this.isToday(date)) {
      return false; // Only check past time for today
    }

    const timezone = this.getLocationTimezone();
    if (timezone) {
      return parseTimeToMinutes(time) <= this.getShopCurrentMinutes(timezone);
    }

    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    const selectedDateTime = new Date();
    selectedDateTime.setHours(hours, minutes, 0, 0);

    return selectedDateTime <= now;
  }

  isDateDisabled(date: Date): boolean {
    // Only disable past dates if allowPastDates is false
    const isPastDisabled = !this.allowPastDates && this.isPastDate(date);
    if (isPastDisabled || !this.isDayAvailable(date) || this.isOutsideMinMaxRange(date)) {
      return true;
    }

    const timezone = this.getLocationTimezone();
    return (
      !!timezone &&
      this.showsTimePicker &&
      this.isToday(date) &&
      !this.hasRemainingTimeSlotForDate(date, timezone)
    );
  }

  private isOutsideMinMaxRange(date: Date): boolean {
    const day = new Date(date);
    day.setHours(0, 0, 0, 0);

    if (this.minDate) {
      const min = new Date(this.minDate);
      min.setHours(0, 0, 0, 0);
      if (day.getTime() < min.getTime()) {
        return true;
      }
    }

    if (this.maxDate) {
      const max = new Date(this.maxDate);
      max.setHours(0, 0, 0, 0);
      if (day.getTime() > max.getTime()) {
        return true;
      }
    }

    return false;
  }

  /**
   * Determine whether the location is available (not closed) on the given date.
   * @example
   * sDayAvailable(new Date('2026-03-17'))
   * true
   * @param {{Date}} {{date}} - The Date object representing the day to check availability for.
   * @returns {{boolean}} Returns true if the location is open on that day or if no hours are defined; false if explicitly closed.
   **/
  isDayAvailable(date: Date): boolean {
    if (!this.locationDetails || !this.locationDetails.hours) {
      return true; // If no location details, assume all days are available
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    // Find operating hours for this day
    const dayHours = this.locationDetails.hours.find(
      (hours: any) => hours.day.toLowerCase() === dayName
    );

    if (!dayHours) {
      return true; // If no hours specified, assume available
    }

    const isAvailable = !dayHours.is_closed;
    // console.log(`📅 Day ${dayName} availability:`, { isAvailable, dayHours });
    return isAvailable; // Available if not closed
  }

  /**
   * Determine whether a given time slot is available on a specific date based on the location's operating hours.
   * @example
   * sTimeSlotAvailableForDate({ time: '09:30' }, new Date('2026-03-17'))
   * true
   * @param {TimeSlot} timeSlot - TimeSlot object with a 'time' string in "HH:MM" format.
   * @param {Date} date - Date object representing the day to check availability for.
   * @returns {boolean} True if the time slot falls within the location's operating hours for the given date, otherwise false.
   **/
  isTimeSlotAvailableForDate(timeSlot: TimeSlot, date: Date): boolean {
    // if (!timeSlot.available) {
    //   return false;
    // }

    if (!this.locationDetails || !this.locationDetails.hours) {
      return true; // If no location details, use default availability
    }

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];

    // Find operating hours for this day
    const dayHours = this.locationDetails.hours.find(
      (hours: any) => hours.day.toLowerCase() === dayName
    );

    if (!dayHours || dayHours.is_closed) {
      return false; // Not available if closed or no hours
    }

    if (!dayHours.open_time || !dayHours.close_time) {
      return false; // Not available if no specific hours
    }

    // Parse the time slot
    const [hours, minutes] = timeSlot.time.split(':').map(Number);
    const timeInMinutes = hours * 60 + minutes;

    // Parse opening and closing times
    const openTimeInMinutes = this.parseTimeToMinutes(dayHours.open_time);
    const closeTimeInMinutes = this.parseTimeToMinutes(dayHours.close_time);

    const isAvailable = timeInMinutes >= openTimeInMinutes && timeInMinutes <= closeTimeInMinutes;
    // Check if the time slot is within operating hours
    return isAvailable;
  }

  private parseTimeToMinutes(timeString: string): number {
    return parseTimeToMinutes(timeString);
  }

  isTimeSlotDisabled(timeSlot: TimeSlot): boolean {
    const selectedDate = this.selectedDate || this.getCurrentDate();
    if (!this.isTimeSlotAvailableForDate(timeSlot, selectedDate)) {
      return true;
    }
    // Selected-state visibility is separate from availability: keep the current
    // value enabled/highlighted even when it falls outside the live minDate /
    // lead window (e.g. Edit with a saved historical Start/End).
    if (this.selectedTime && timeSlot.time === this.selectedTime) {
      return false;
    }
    if (this.isTimeSlotBeforeMinDate(timeSlot, selectedDate)) {
      return true;
    }
    if (this.isTimeSlotWithinMinLead(timeSlot, selectedDate)) {
      return true;
    }
    // Past-time blocking is for booking flows; allowPastDates opts out (e.g. restrictions).
    if (this.allowPastDates) {
      return false;
    }
    return this.isPastTime(timeSlot.time, selectedDate);
  }

  /**
   * When minDate includes a time (e.g. Start + 30 minutes), disable slots on that
   * same calendar day that fall before the min datetime.
   */
  private isTimeSlotBeforeMinDate(timeSlot: TimeSlot, selectedDate: Date): boolean {
    if (!this.minDate) {
      return false;
    }

    const timezone = this.getLocationTimezone();
    const [hours, minutes] = timeSlot.time.split(':').map(Number);
    const slotDateTime = timezone
      ? this.createDateInTimezone(selectedDate, hours, minutes, timezone)
      : (() => {
          const dateTime = new Date(selectedDate);
          dateTime.setHours(hours, minutes, 0, 0);
          return dateTime;
        })();

    if (timezone) {
      const selectedKey = this.getDateKey(selectedDate);
      const minKey = this.getDateKey(this.getDateInTimezone(this.minDate, timezone));
      if (selectedKey < minKey) {
        return true;
      }
      if (selectedKey > minKey) {
        return false;
      }
      return slotDateTime.getTime() < this.minDate.getTime();
    }

    const selectedDay = new Date(selectedDate);
    selectedDay.setHours(0, 0, 0, 0);
    const minDay = new Date(this.minDate);
    minDay.setHours(0, 0, 0, 0);

    if (selectedDay.getTime() < minDay.getTime()) {
      return true;
    }
    if (selectedDay.getTime() > minDay.getTime()) {
      return false;
    }

    return slotDateTime.getTime() < this.minDate.getTime();
  }

  /**
   * Disable slots on today that are earlier than (now + minLeadMinutes).
   * Future dates are unaffected. When `locationDetails.timezone` is set, the slot
   * instant is built in that shop zone (same source as CREATED DATE / x-timezone).
   */
  private isTimeSlotWithinMinLead(timeSlot: TimeSlot, selectedDate: Date): boolean {
    if (this.minLeadMinutes <= 0 || !this.isToday(selectedDate)) {
      return false;
    }

    const [hours, minutes] = timeSlot.time.split(':').map(Number);
    const timezone = this.getLocationTimezone();
    const slotDateTime = timezone
      ? this.createDateInTimezone(selectedDate, hours, minutes, timezone)
      : (() => {
          const dateTime = new Date(selectedDate);
          dateTime.setHours(hours, minutes, 0, 0);
          return dateTime;
        })();

    const earliest = addMinutesToDate(new Date(), this.minLeadMinutes);
    return slotDateTime.getTime() < earliest.getTime();
  }

  getVisibleTimeSlots(): TimeSlot[] {
    return this.timeSlots;
  }

  private hasRemainingTimeSlotForDate(date: Date, timezone: string): boolean {
    return this.timeSlots.some(slot => {
      if (!this.isTimeSlotAvailableForDate(slot, date)) {
        return false;
      }
      if (this.isTimeSlotWithinMinLead(slot, date)) {
        return false;
      }
      if (!this.allowPastDates && this.isPastTime(slot.time, date)) {
        return false;
      }
      // When past dates are allowed, still hide a fully-elapsed today once every
      // slot is at or before shop "now" (minLead already covered above).
      if (
        this.allowPastDates &&
        parseTimeToMinutes(slot.time) <= this.getShopCurrentMinutes(timezone)
      ) {
        return false;
      }
      return true;
    });
  }

  getCurrentDate(): Date {
    const timezone = this.getLocationTimezone();
    if (timezone) {
      const { year, month, day } = this.getZonedDateTimeParts(new Date(), timezone);
      return new Date(year, month - 1, day);
    }

    return new Date();
  }

  private getLocationTimezone(): string {
    const timezone = this.locationDetails?.timezone;
    return typeof timezone === 'string' && timezone.trim() ? timezone.trim() : '';
  }

  private getDateParts(date: Date): { year: number; month: number; day: number } {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private getDateKey(date: Date): string {
    const { year, month, day } = this.getDateParts(date);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private getShopTodayKey(timezone: string): string {
    const { year, month, day } = this.getZonedDateTimeParts(new Date(), timezone);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private getShopCurrentMinutes(timezone: string): number {
    const { hour, minute } = this.getZonedDateTimeParts(new Date(), timezone);
    return hour * 60 + minute;
  }

  private getZonedDateTimeParts(
    date: Date,
    timezone: string
  ): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
    const formatter = this.getZonedDateTimeFormatter(timezone);
    const values = formatter.formatToParts(date).reduce<Record<string, number>>((acc, part) => {
      if (part.type !== 'literal') {
        acc[part.type] = Number(part.value);
      }
      return acc;
    }, {});

    return {
      year: values['year'],
      month: values['month'],
      day: values['day'],
      hour: values['hour'],
      minute: values['minute'],
      second: values['second'] ?? 0,
    };
  }

  private getZonedDateTimeFormatter(timezone: string): Intl.DateTimeFormat {
    let formatter = this.zonedDateTimeFormatters.get(timezone);
    if (!formatter) {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      };
      try {
        formatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: timezone });
      } catch {
        formatter = new Intl.DateTimeFormat('en-US', options);
      }
      this.zonedDateTimeFormatters.set(timezone, formatter);
    }
    return formatter;
  }

  isSelectionComplete(): boolean {
    if (this.effectiveMode === 'time') {
      return Boolean(this.selectedTime);
    }
    if (this.effectiveMode === 'date') {
      return Boolean(this.selectedDate);
    }
    return Boolean(this.selectedDate && this.selectedTime);
  }

  /** @deprecated Use isSelectionComplete() */
  isDateAndTimeSelected(): boolean {
    return this.isSelectionComplete();
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
